import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { mockProducts, mockReviews } from '../data/mockProducts';
import { Star, Shield, Truck, CheckCircle, Heart, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [products, setProducts] = useState(mockProducts);
  const [loading, setLoading] = useState(true);
  const whatsappNumber = "918448711937";
  
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      if (response.data && response.data.length > 0) {
        setProducts(response.data);
      }
    } catch (error) {
      console.log('Using mock data');
    } finally {
      setLoading(false);
    }
  };
  
  const openWhatsApp = (productName = "") => {
    const message = productName 
      ? `I want to order ${productName} from Brown Bag Dry Fruits.`
      : "I want to order from Brown Bag Dry Fruits.";
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1e1919]/95 backdrop-blur-sm border-b border-[#61525a]/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#c9a961] to-[#8b6f3d] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">BB</span>
            </div>
            <h1 className="text-xl font-bold text-white">Brown Bag Dry Fruits</h1>
          </div>
          <Button 
            onClick={() => openWhatsApp()}
            className="bg-[#25d366] hover:bg-[#20bd5a] text-white transition-all duration-300"
          >
            Order on WhatsApp
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#f7f5f2] to-[#e8e3db]">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="inline-block mb-6 px-4 py-2 bg-[#61525a]/10 rounded-full">
            <span className="text-[#61525a] font-medium">Premium Quality Since Years</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-[#1e1919] mb-6 leading-tight">
            Premium Quality Dry Fruits
            <br />
            <span className="text-[#8b6f3d]">Delivered Across India</span>
          </h2>
          <p className="text-xl text-[#736c64] mb-8 flex flex-wrap items-center justify-center gap-3">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#8b6f3d]" />
              Fresh
            </span>
            <span className="text-[#61525a]">•</span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#8b6f3d]" />
              Hygienic
            </span>
            <span className="text-[#61525a]">•</span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#8b6f3d]" />
              Nutritious
            </span>
            <span className="text-[#61525a]">•</span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#8b6f3d]" />
              Handpicked Quality
            </span>
          </p>
          <p className="text-lg text-[#1e1919] mb-8 font-medium">
            Enjoy Your Healthy Bites
          </p>
          <Button 
            onClick={() => openWhatsApp()}
            size="lg"
            className="bg-gradient-to-r from-[#8b6f3d] to-[#c9a961] hover:from-[#7a5f35] hover:to-[#b8984f] text-white px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Order on WhatsApp
          </Button>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-[#1e1919] mb-4">Our Premium Collection</h3>
            <p className="text-[#736c64] text-lg">Handpicked quality dry fruits and healthy snacks</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="group overflow-hidden border-[#61525a]/10 hover:border-[#8b6f3d]/30 transition-all duration-300 hover:shadow-xl">
                <div className="relative overflow-hidden aspect-square">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-[#8b6f3d] text-white px-3 py-1 rounded-full text-sm font-medium">
                    {product.weight}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h4 className="text-xl font-bold text-[#1e1919] mb-2">{product.name}</h4>
                  <p className="text-[#736c64] text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-[#8b6f3d]">
                      {product.price ? `₹${product.price}` : 'Price on Request'}
                    </span>
                  </div>
                  <Button 
                    onClick={() => openWhatsApp(product.name)}
                    className="w-full bg-[#25d366] hover:bg-[#20bd5a] text-white transition-all duration-300"
                  >
                    Order on WhatsApp
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-[#f7f5f2]">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-[#1e1919] mb-4">Why Choose Us</h3>
            <p className="text-[#736c64] text-lg">Quality and trust you can count on</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Award, title: "Premium Quality", desc: "Only the finest handpicked products" },
              { icon: Shield, title: "Hygienic Packing", desc: "Sealed with care for freshness" },
              { icon: Heart, title: "Fresh Stock", desc: "Always fresh, never stale products" },
              { icon: Truck, title: "Free Shipping", desc: "Delivery across India at no extra cost" },
              { icon: Star, title: "Trusted by Customers", desc: "83 happy customers on Google" },
              { icon: CheckCircle, title: "Quality Assured", desc: "100% satisfaction guaranteed" }
            ].map((feature, idx) => (
              <div key={idx} className="text-center p-6 bg-white rounded-lg hover:shadow-lg transition-all duration-300 border border-[#61525a]/10">
                <div className="w-16 h-16 bg-gradient-to-br from-[#8b6f3d] to-[#c9a961] rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-[#1e1919] mb-2">{feature.title}</h4>
                <p className="text-[#736c64]">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-[#c9a961] text-[#c9a961]" />
              ))}
            </div>
            <h3 className="text-4xl font-bold text-[#1e1919] mb-2">Rated by 83 Happy Customers</h3>
            <p className="text-[#736c64] text-lg">See what our customers say on Google Reviews</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockReviews.map((review) => (
              <Card key={review.id} className="border-[#61525a]/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#c9a961] text-[#c9a961]" />
                    ))}
                  </div>
                  <p className="text-[#1e1919] mb-4 italic">"{review.comment}"</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-[#1e1919]">{review.name}</span>
                    <span className="text-[#736c64]">{review.date}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-20 px-4 bg-[#1e1919] text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-4xl font-bold mb-6">About Brown Bag Dry Fruits</h3>
          <p className="text-[#bbb5ae] text-lg leading-relaxed">
            Brown Bag Dry Fruits provides carefully selected premium dry fruits and healthy snacks 
            sourced from the best regions. We focus on quality, freshness, hygienic packing, and 
            customer satisfaction across India. Every product is handpicked to ensure you receive 
            only the finest quality that nature has to offer.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#f7f5f2] to-[#e8e3db]">
        <div className="container mx-auto max-w-2xl text-center">
          <h3 className="text-4xl font-bold text-[#1e1919] mb-6">Order Now</h3>
          <p className="text-[#736c64] text-lg mb-8">
            Fast response & easy ordering through WhatsApp
          </p>
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <p className="text-2xl font-bold text-[#8b6f3d] mb-4">Order on WhatsApp</p>
            <p className="text-3xl font-bold text-[#1e1919] mb-6">+91 8448711937</p>
            <Button 
              onClick={() => openWhatsApp()}
              size="lg"
              className="bg-[#25d366] hover:bg-[#20bd5a] text-white px-8 py-6 text-lg transition-all duration-300"
            >
              Start Ordering Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-8 px-4 border-t border-[#61525a]/20">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-[#c9a961] to-[#8b6f3d] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">BB</span>
            </div>
            <span className="font-bold">Brown Bag Dry Fruits</span>
          </div>
          <p className="text-[#bbb5ae] mb-2">Enjoy Your Healthy Bites</p>
          <p className="text-[#bbb5ae] text-sm">
            Premium Quality Dry Fruits • Free Shipping Across India
          </p>
          <p className="text-[#736c64] text-sm mt-4">
            © 2024 Brown Bag Dry Fruits. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
